import { useState, useEffect } from 'react';

type SetValue<T> = T | ((val: T) => T);

interface LocalStorageOptions<T> {
  storageKeys?: (keyof T)[];
}

export const useLocalStorage = <T>(
  key: string,
  initialValue: T,
  options?: LocalStorageOptions<T>,
): [T, (value: SetValue<T>) => void] => {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      if (typeof window === 'undefined') {
        return initialValue;
      }
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(error);
      return initialValue;
    }
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      let valueToStore = storedValue;
      if (options?.storageKeys) {
        valueToStore = {} as T;
        options.storageKeys.forEach((storageKey) => {
          (valueToStore as any)[storageKey] = storedValue[storageKey];
        });
      }

      try {
        window.localStorage.setItem(key, JSON.stringify(valueToStore));
      } catch (error) {
        // LocalStorage may not be available in some contexts
      }
    }
  }, [key, storedValue, options?.storageKeys]);

  return [storedValue, setStoredValue];
};
